// src/shared/utils/brand.ts
//
// Pre-login brand identity, derived from the request hostname. The dashboard is
// served under two login hosts: my.streamhoster.com (direct customers) and
// my.themediacdn.com (white-label resellers and their customers). Post-login logo
// selection is account-based (see ExtPlatConfig / cfg.reseller); this module
// governs only the UNAUTHENTICATED screens, where no account exists yet.

export const BRAND_VARIANTS = ['streamhoster', 'whitelabel'] as const;
export type BrandVariant = typeof BRAND_VARIANTS[number];

/** Per-brand legal links shown on the auth screens (host-selected, matching legacy). */
export const BRAND_LEGAL_LINKS: Record<BrandVariant, { privacy: string; terms: string }> = {
  streamhoster: {
    privacy: 'https://streamhoster.com/pp',
    terms: 'https://streamhoster.com/tos',
  },
  whitelabel: {
    privacy: 'https://static.themediacdn.com/tmcdn/pp.html',
    terms: 'https://static.themediacdn.com/tmcdn/tos.html',
  },
};

export class Brand {
  private static readonly STREAMHOSTER_HOST = 'streamhoster.com';

  /**
   * Resolve the brand variant from the current hostname — SYNCHRONOUSLY, so the
   * correct screen renders on the first paint (never a default-then-correct swap,
   * i.e. no branding flash). Safe default is 'whitelabel': Streamhoster branding
   * requires a POSITIVE streamhoster.com hostname match, so a visitor on
   * my.themediacdn.com (or any non-Streamhoster host) can never be shown, even
   * transiently, the Streamhoster branding.
   */
  static variant(): BrandVariant {
    if (typeof window === 'undefined') return 'whitelabel';
    return window.location.hostname.toLowerCase().includes(Brand.STREAMHOSTER_HOST)
      ? 'streamhoster'
      : 'whitelabel';
  }
}
